import { formatYmMonthShort } from "./formatYmMonthShort";

export type PeriodGrouping = "month" | "quarter" | "year";

export type EmailSendPeriodGrouping = "day" | "week" | "month";

export const PERIOD_GROUPING_OPTIONS: ReadonlyArray<{
  value: PeriodGrouping;
  label: string;
}> = [
  { value: "month", label: "Monthly" },
  { value: "quarter", label: "Quarterly" },
  { value: "year", label: "Annually" },
];

export const EMAIL_SEND_PERIOD_GROUPING_OPTIONS: ReadonlyArray<{
  value: EmailSendPeriodGrouping;
  label: string;
}> = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

function parseYearMonth(
  monthKey: string,
): { year: number; month: number } | null {
  const [yearString, monthString] = monthKey.split("-");
  const year = Number(yearString);
  const month = Number(monthString);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
  return { year, month };
}

function quarterFromMonth(month: number): number {
  return Math.floor((month - 1) / 3) + 1;
}

export function getGroupingBucket(
  monthKey: string,
  grouping: PeriodGrouping,
): { key: string; label: string } {
  if (grouping === "month") {
    return { key: monthKey, label: formatYmMonthShort(monthKey) };
  }

  const parsed = parseYearMonth(monthKey);
  if (!parsed) return { key: monthKey, label: monthKey };

  if (grouping === "year") {
    return { key: `${parsed.year}`, label: `${parsed.year}` };
  }

  const quarter = quarterFromMonth(parsed.month);
  return {
    key: `${parsed.year}-Q${quarter}`,
    label: `Q${quarter} ${parsed.year}`,
  };
}

export function formatCollectionPeriodLabel(
  period: string,
  grouping: PeriodGrouping,
): string {
  if (grouping !== "month") return period;
  return getGroupingBucket(period, "month").label;
}

function parsePeriodDateParts(
  period: string,
): { year: number; month: number; day: number } | null {
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(period.trim());
  if (ymd) {
    const year = Number(ymd[1]);
    const month = Number(ymd[2]);
    const day = Number(ymd[3]);
    if (
      !Number.isFinite(year) ||
      !Number.isFinite(month) ||
      !Number.isFinite(day) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31
    ) {
      return null;
    }
    return { year, month, day };
  }

  const ym = /^(\d{4})-(\d{2})$/.exec(period.trim());
  if (!ym) return null;
  const year = Number(ym[1]);
  const month = Number(ym[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return null;
  }
  return { year, month, day: 1 };
}

/** Format email send-volume bucket keys (`YYYY-MM-DD` or `YYYY-MM`) for chart axes. */
export function formatEmailSendPeriodLabel(
  period: string,
  grouping: EmailSendPeriodGrouping,
): string {
  const parts = parsePeriodDateParts(period);
  if (!parts) return period;

  if (grouping === "month") {
    return formatYmMonthShort(
      `${parts.year}-${String(parts.month).padStart(2, "0")}`,
    );
  }

  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}
